import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import ErrorBoundary from './components/ErrorBoundary';

// Pages
import Welcome from './pages/Welcome';
import Auth from './pages/Auth';
import ClientDashboard from './pages/ClientDashboard';
import ProfessionalDashboard from './pages/ProfessionalDashboard';
import JobPosting from './pages/JobPosting';
import JobDetails from './pages/JobDetails';
import ProfessionalProfile from './pages/ProfessionalProfile';
import Chat from './pages/Chat';
import ChatList from './pages/ChatList';
import ProfileSettings from './pages/ProfileSettings';
import Subscription from './pages/Subscription';
import TermsPolicy from './pages/TermsPolicy';
import AdminDashboard from './pages/AdminDashboard';
import VerificationManagement from './pages/VerificationManagement';
import AdminUserManagement from './pages/AdminUserManagement';
import AdminJobManagement from './pages/AdminJobManagement';
import AdminSupportTickets from './pages/AdminSupportTickets';
import AdminUserDetails from './pages/AdminUserDetails';
import AdminAnnouncements from './pages/AdminAnnouncements';
import AuditLogs from './pages/AuditLogs';
import AdminAds from './pages/AdminAds';
import AdminUserActivity from './pages/AdminUserActivity';
import SubscriptionRequired from './pages/SubscriptionRequired';
import SubscriptionPlans from './pages/SubscriptionPlans';
import SaferpaySubscription from './pages/SaferpaySubscription';
import PaymentCallback from './pages/PaymentCallback';
import VerificationFee from './pages/VerificationFee';
import VerificationInstructions from './pages/VerificationInstructions';
import IdentityVerification from './pages/IdentityVerification';
import PricingPage from './pages/PricingPage';
import AddRolePage from './pages/AddRolePage';
import NotificationsPage from './pages/NotificationsPage';
import NotFound from './pages/NotFound';
import { SeoManager } from './components/SeoManager';
import config from './config';

// Use hardcoded production URL from config
const API = config.API_URL;

const getToken = () => localStorage.getItem('helply_token');

// Configure axios to prevent caching for PWA
axios.defaults.headers.common['Cache-Control'] = 'no-cache, no-store, must-revalidate';
axios.defaults.headers.common['Pragma'] = 'no-cache';
axios.defaults.headers.common['Expires'] = '0';

// Add request interceptor to add timestamp to prevent caching
axios.interceptors.request.use((config) => {
  config.params = { ...config.params, _t: Date.now() };
  if (!config.headers['Authorization']) {
    const token = getToken();
    if (token) config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
}, (error) => Promise.reject(error));

// Auth Context
export const AuthContext = React.createContext();

// Protected Route Component — supports multi-role via activeRole
const ProtectedRoute = ({ children, requiredRole }) => {
  const token = getToken();
  const location = useLocation();
  const user = (() => {
    try { return JSON.parse(localStorage.getItem('helply_user') || 'null'); }
    catch { return null; }
  })();
  const activeRole = localStorage.getItem('helply_active_role') || (user ? user.role : null);

  if (!token || !user) {
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to="/auth" replace state={{ returnTo }} />;
  }

  // Admin always bypasses subscription check
  if (!user.is_admin) {
    const roleSubscription = user.role_subscriptions?.[activeRole] || {};
    const status = roleSubscription.status || user.subscription_status;
    const paidThrough = roleSubscription.ends_at || user.subscription_ends_at;
    const paidAccessRemaining = paidThrough && new Date(paidThrough) > new Date();
    const trialEnd = user.trial_ends_at;
    const trialExpired = trialEnd && new Date(trialEnd) < new Date();

    // Block if: explicitly blocked by admin, or a subscription that was once active has
    // lapsed (expired/cancelled), or trial period ended without paying. Never-subscribed
    // ('inactive') users are NOT blocked here — they can browse/complete their profile and
    // submit verification documents for free; payment is only required to post a job or bid
    // (enforced by the backend on those specific actions).
    const isBlocked =
      status === 'blocked' ||
      status === 'expired' ||
      (status === 'cancelled' && !paidAccessRemaining) ||
      (status === 'trial' && trialExpired);

    // /settings and /subscription-plans must stay reachable so users can view their
    // plan status and subscribe
    const recoveryPaths = ['/settings', '/subscription-plans', '/subscription-payment', '/saferpay-subscription', '/subscription-callback', '/verification-callback', '/identity-verification', '/verification-instructions', '/verification-fee'];
    const isRecoveryPath = recoveryPaths.includes(location.pathname) || location.pathname.startsWith('/add-role/');
    if (isBlocked && !isRecoveryPath) return <Navigate to="/subscription-required" replace />;
  }

  if (requiredRole) {
    const userRoles = user.roles || [user.role];
    if (!userRoles.includes(requiredRole)) return <Navigate to="/" replace />;
  }

  return children;
};

function App() {
  // Layer 3: instant load from cached user — no API call needed on init
  const [user, setUser] = useState(() => {
    try {
      const cached = localStorage.getItem('helply_user');
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState('en');
  const [activeRole, setActiveRole] = useState(() => {
    return localStorage.getItem('helply_active_role') || null;
  });

  // Persist user data whenever it changes
  useEffect(() => {
    if (user) {
      localStorage.setItem('helply_user', JSON.stringify(user));
      // Initialize activeRole from user.role if not set yet
      if (!localStorage.getItem('helply_active_role')) {
        const role = user.role;
        setActiveRole(role);
        localStorage.setItem('helply_active_role', role);
      }
    }
  }, [user]);

  const switchRole = (role) => {
    setActiveRole(role);
    localStorage.setItem('helply_active_role', role);
  };

  const refreshUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      const userData = res.data;
      setUser(userData);
      localStorage.setItem('helply_user', JSON.stringify(userData));
      return userData;
    } catch {}
  };

  useEffect(() => {
    const initializeAuth = async () => {
      const token = getToken();
      const cachedUser = localStorage.getItem('helply_user');
      const savedLang = localStorage.getItem('helply_language') || 'en';

      setLanguage(savedLang);
      if (savedLang === 'ar') document.body.setAttribute('dir', 'rtl');
      else document.body.setAttribute('dir', 'ltr');
      document.body.setAttribute('lang', savedLang);

      // No token AND no cache = never logged in
      if (!token && !cachedUser) {
        setLoading(false);
        return;
      }

      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }

      // If no valid token but cache exists, show user immediately
      if (!token && cachedUser) {
        try { setUser(JSON.parse(cachedUser)); } catch {}
        setLoading(false);
        return;
      }

      try {
        // Validate token + auto-refresh via /auth/me
        const meResponse = await axios.get(`${API}/auth/me`);
        const userData = meResponse.data;

        // Auto-refresh token if backend issued a new one
        if (userData.refreshed_token) {
          localStorage.setItem('helply_token', userData.refreshed_token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${userData.refreshed_token}`;
          delete userData.refreshed_token;
        }

        setUser(userData);

        // Firebase auth restore
        try {
          const firebaseResponse = await axios.get(`${API}/users/me/firebase-token`);
          if (firebaseResponse.data.firebase_token) {
            const { signInToFirebase } = await import('./services/firebaseAuth');
            await signInToFirebase(firebaseResponse.data.firebase_token);
          }
        } catch {}
      } catch (err) {
        // NEVER log out on network error — keep cached session
        if (cachedUser) {
          try { setUser(JSON.parse(cachedUser)); } catch {}
        } else {
          // Token is clearly invalid (401) and no cache — log out
          const status = err?.response?.status;
          if (status === 401) {
            localStorage.removeItem('helply_token');
            localStorage.removeItem('helply_user');
            delete axios.defaults.headers.common['Authorization'];
            setUser(null);
          }
          // For 5xx / network errors: keep user logged in
        }
      }

      setLoading(false);
    };

    initializeAuth();

    // Clear browser cache on app startup for PWA
    if (window.caches) {
      caches.keys().then(names => names.forEach(name => caches.delete(name)));
    }
  }, []);

  // ── Tab visibility refresh (5s debounce) ───────────────────────────────────
  useEffect(() => {
    let timer = null;
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && getToken()) {
        clearTimeout(timer);
        timer = setTimeout(async () => {
          try {
            const res = await axios.get(`${API}/auth/me`);
            const userData = res.data;
            if (userData.refreshed_token) {
              localStorage.setItem('helply_token', userData.refreshed_token);
              axios.defaults.headers.common['Authorization'] = `Bearer ${userData.refreshed_token}`;
              delete userData.refreshed_token;
            }
            setUser(userData);
          } catch {} // Silently ignore — keep current session
        }, 5000);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      clearTimeout(timer);
    };
  }, []);

  const login = async (token, userData, firebaseToken) => {
    localStorage.setItem('helply_token', token);
    localStorage.setItem('helply_user', JSON.stringify(userData));
    // Set activeRole from user's primary role on login
    const role = userData.role;
    setActiveRole(role);
    localStorage.setItem('helply_active_role', role);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);

    // Sign in to Firebase if token provided
    if (firebaseToken) {
      try {
        const { signInToFirebase } = await import('./services/firebaseAuth');
        await signInToFirebase(firebaseToken);
      } catch (error) {
        console.error('Firebase authentication failed:', error);
      }
    }

    // Request notification permissions
    try {
      const { requestNotificationPermission } = await import('./services/fcmService');
      await requestNotificationPermission(API);
    } catch (error) {
      console.error('Failed to request notification permission:', error);
    }
  };

  const logout = async () => {
    localStorage.removeItem('helply_token');
    localStorage.removeItem('helply_user');
    localStorage.removeItem('helply_active_role');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);

    // Sign out from Firebase
    try {
      const { signOutFromFirebase } = await import('./services/firebaseAuth');
      await signOutFromFirebase();
    } catch (error) {
      console.error('Firebase sign-out error:', error);
    }
  };

  const updateLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('helply_language', lang);
    // Set RTL for Arabic
    if (lang === 'ar') {
      document.body.setAttribute('dir', 'rtl');
    } else {
      document.body.setAttribute('dir', 'ltr');
    }
    // Set language attribute for font selection
    document.body.setAttribute('lang', lang);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <AuthContext.Provider value={{ user, login, logout, language, updateLanguage, API, activeRole, switchRole, refreshUser }}>
        <BrowserRouter>
          <SeoManager />
          <Toaster position="bottom-right" richColors />
          <Routes>
          <Route path="/" element={user ? (
            user.is_admin ? <Navigate to="/admin/dashboard" /> :
            activeRole === 'professional' ? <Navigate to="/dashboard/professional" /> :
            activeRole === 'client' ? <Navigate to="/dashboard/client" /> :
            user.role === 'client' ? <Navigate to="/dashboard/client" /> :
            <Navigate to="/dashboard/professional" />
          ) : <Welcome />} />
          <Route path="/auth" element={!user ? <Auth /> : <Navigate to="/" />} />

          {/* Pricing page - public */}
          <Route path="/pricing" element={<PricingPage />} />

          {/* Add Role flow - requires login */}
          <Route path="/add-role/:targetRole" element={
            <ProtectedRoute>
              <AddRolePage />
            </ProtectedRoute>
          } />

          {/* Notifications full page */}
          <Route path="/notifications" element={
            <ProtectedRoute>
              <NotificationsPage />
            </ProtectedRoute>
          } />
          
          <Route path="/dashboard/client" element={
            <ProtectedRoute requiredRole="client">
              <ClientDashboard />
            </ProtectedRoute>
          } />

          <Route path="/dashboard/professional" element={
            <ProtectedRoute requiredRole="professional">
              <ProfessionalDashboard />
            </ProtectedRoute>
          } />

          <Route path="/post-job" element={
            <ProtectedRoute requiredRole="client">
              <JobPosting />
            </ProtectedRoute>
          } />
          
          <Route path="/job/:jobId" element={
            <ProtectedRoute>
              <JobDetails />
            </ProtectedRoute>
          } />
          
          <Route path="/professional/:userId" element={
            <ProtectedRoute>
              <ProfessionalProfile />
            </ProtectedRoute>
          } />
          
          <Route path="/chats" element={
            <ProtectedRoute>
              <ChatList />
            </ProtectedRoute>
          } />
          
          <Route path="/chat/:jobId" element={
            <ProtectedRoute>
              <Chat />
            </ProtectedRoute>
          } />
          
          <Route path="/settings" element={
            <ProtectedRoute>
              <ProfileSettings />
            </ProtectedRoute>
          } />
          
          <Route path="/subscription" element={
            <ProtectedRoute>
              <Subscription />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/dashboard" element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/verifications" element={
            <ProtectedRoute>
              <VerificationManagement />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/users" element={
            <ProtectedRoute>
              <AdminUserManagement />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/jobs" element={
            <ProtectedRoute>
              <AdminJobManagement />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/support" element={
            <ProtectedRoute>
              <AdminSupportTickets />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/audit-logs" element={
            <ProtectedRoute>
              <AuditLogs />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/announcements" element={
            <ProtectedRoute>
              <AdminAnnouncements />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/ads" element={
            <ProtectedRoute>
              <AdminAds />
            </ProtectedRoute>
          } />

          <Route path="/admin/user-activity" element={
            <ProtectedRoute>
              <AdminUserActivity />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/users/:userId" element={
            <ProtectedRoute>
              <AdminUserDetails />
            </ProtectedRoute>
          } />
          
          <Route path="/terms-policy" element={<TermsPolicy />} />
          
          <Route path="/subscription-required" element={<SubscriptionRequired />} />
          
          <Route path="/subscription-plans" element={
            <ProtectedRoute>
              <SubscriptionPlans />
            </ProtectedRoute>
          } />
          
          <Route path="/subscription-payment" element={
            <ProtectedRoute>
              <SaferpaySubscription />
            </ProtectedRoute>
          } />
          
          <Route path="/saferpay-subscription" element={
            <ProtectedRoute>
              <SaferpaySubscription />
            </ProtectedRoute>
          } />
          
          <Route path="/verification-callback" element={
            <ProtectedRoute>
              <PaymentCallback />
            </ProtectedRoute>
          } />
          
          <Route path="/subscription-callback" element={
            <ProtectedRoute>
              <PaymentCallback />
            </ProtectedRoute>
          } />
          
          <Route path="/verification-fee" element={
            <ProtectedRoute requiredRole="professional">
              <VerificationFee />
            </ProtectedRoute>
          } />
          
          <Route path="/verification-instructions" element={
            <ProtectedRoute requiredRole="professional">
              <VerificationInstructions />
            </ProtectedRoute>
          } />
          
          <Route path="/identity-verification" element={
            <ProtectedRoute requiredRole="professional">
              <IdentityVerification />
            </ProtectedRoute>
          } />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </AuthContext.Provider>
    </ErrorBoundary>
  );
}

export default App;
export { API };